import { useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./ServicesSelectionStep.module.scss";
import { ServiceCard } from "./ServiceCard/ServiceCard";
import { ServiceCategoryHeader } from "./ServiceCategoryHeader/ServiceCategoryHeader";
import { IService, IServiceCategory } from "../../models/IService";
import { AppAccordion, IAppAccordionTab } from "../../../../../common/components/AppAccordion/AppAccordion";

interface IServicesSelectionStepProps {
  className?: string;
  services: IService[];
  categories: IServiceCategory[];
  servicesByCategory: Record<number | "uncategorized", IService[]>;
  selectedService?: IService | null;
  onServiceSelect: (service: IService) => void;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  loadingText?: string;
  emptyStateText?: string;
  isGroupClassMode?: boolean;
  countLabelSingular?: string;
  countLabelPlural?: string;
}

export const ServicesSelectionStep = ({
  className,
  services,
  categories,
  servicesByCategory,
  selectedService,
  onServiceSelect,
  isLoading = false,
  title = "Услуги",
  subtitle = "Изберете услугата, която искате да резервирате",
  loadingText = "Зареждане на услуги...",
  emptyStateText = "В момента няма налични услуги",
  isGroupClassMode = false,
  countLabelSingular = "услуга",
  countLabelPlural = "услуги",
}: IServicesSelectionStepProps) => {
  // Build accordion tabs from categories and track which tab contains the selected service
  const { accordionTabs, selectedServiceTabIndex } = useMemo(() => {
    const tabs: IAppAccordionTab[] = [];
    let selectedTabIndex: number | null = null;

    // Add tabs for each category (sorted by sortOrder from backend)
    categories.forEach((category) => {
      const categoryServices = servicesByCategory[category.id] || [];
      if (categoryServices.length === 0) return; // Skip empty categories

      // Check if selected service is in this category
      if (selectedService && categoryServices.some(s => s.id === selectedService.id)) {
        selectedTabIndex = tabs.length; // Current index before pushing
      }

      tabs.push({
        id: category.id,
        header: (
          <ServiceCategoryHeader
            name={category.name}
            serviceCount={categoryServices.length}
            countLabelSingular={countLabelSingular}
            countLabelPlural={countLabelPlural}
          />
        ),
        content: (
          <div className={styles.categoryContent}>
            {categoryServices.map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                isSelected={selectedService?.id === service.id}
                onClick={() => onServiceSelect(service)}
                animationDelay={index * 50}
                isGroupClassMode={isGroupClassMode}
              />
            ))}
          </div>
        ),
      });
    });

    // Add "Други услуги" tab for uncategorized services
    const uncategorizedServices = servicesByCategory.uncategorized || [];
    if (uncategorizedServices.length > 0) {
      // Check if selected service is uncategorized
      if (selectedService && uncategorizedServices.some(s => s.id === selectedService.id)) {
        selectedTabIndex = tabs.length; // Current index before pushing
      }

      tabs.push({
        id: "uncategorized",
        header: (
          <ServiceCategoryHeader
            name="Други услуги"
            serviceCount={uncategorizedServices.length}
            countLabelSingular={countLabelSingular}
            countLabelPlural={countLabelPlural}
          />
        ),
        content: (
          <div className={styles.categoryContent}>
            {uncategorizedServices.map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                isSelected={selectedService?.id === service.id}
                onClick={() => onServiceSelect(service)}
                animationDelay={index * 50}
                isGroupClassMode={isGroupClassMode}
              />
            ))}
          </div>
        ),
      });
    }

    return { accordionTabs: tabs, selectedServiceTabIndex: selectedTabIndex };
  }, [categories, servicesByCategory, selectedService, onServiceSelect, isGroupClassMode, countLabelSingular, countLabelPlural]);

  // Show accordion only if at least one service is categorized
  // If all services are uncategorized, show flat grid instead
  const hasCategorizedServices = services.some(s => s.categoryId !== null);

  if (isLoading) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>{loadingText}</p>
        </div>
      </div>
    );
  }

  if (!services || services.length === 0) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🔍</div>
          <p className={styles.emptyText}>{emptyStateText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.container, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      {/* Services - Accordion or Flat Grid based on categories */}
      {hasCategorizedServices ? (
        <div className={styles.accordionContainer}>
          <AppAccordion
            tabs={accordionTabs}
            variant="dark"
            multiple={true}
            defaultActiveIndex={selectedServiceTabIndex !== null ? [selectedServiceTabIndex] : []}
          />
        </div>
      ) : (
        <div className={styles.servicesGrid}>
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              isSelected={selectedService?.id === service.id}
              onClick={() => onServiceSelect(service)}
              animationDelay={index * 100}
              isGroupClassMode={isGroupClassMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};
